import { useCallback, useRef } from "react";
import type { UUID } from "../../../__definitions__/iconfinder.changeengine.com-definitions";
import { convertToLettersOnly } from "../../../utils/convert-to-letters-only";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { DisplayConditionData } from "../../unlayer-email-content/components/display-condition-dialog";
import {
  type DisplayConditionRequestData,
  type DisplayConditionReturn,
} from "../../unlayer-email-content/hooks/use-display-condition";
import type { UnlayerDesign } from "../../unlayer-email-content/types/unlayer-design";
import { getDisplayConditionDialogState } from "../../unlayer-email-content/utils/display-condition-nesting";

interface UseFollowUpDisplayConditionOptions {
  getDynamicSegmentIdMap: () => Record<string, string> | null;
  setDynamicSegmentIdMap: (map: Record<string, string>) => void;
  getDesign: () => UnlayerDesign | null;
}

export function useFollowUpDisplayCondition({
  getDynamicSegmentIdMap,
  setDynamicSegmentIdMap,
  getDesign,
}: UseFollowUpDisplayConditionOptions): DisplayConditionReturn {
  const {
    open,
    handleClose,
    handleOpen,
    data: dialogState,
  } = useDialog<{
    nestedBlocked: boolean;
    selectedId?: UUID;
  }>();
  const doneCallbackRef = useRef<((data: DisplayConditionData) => void) | null>(null);

  const onDisplayConditionRequest = useCallback(
    (dcData: DisplayConditionRequestData, done: (data: DisplayConditionData) => void) => {
      doneCallbackRef.current = done;
      handleOpen(getDisplayConditionDialogState(getDesign(), dcData));
    },
    [getDesign, handleOpen],
  );

  const onConfirm = useCallback(
    (conditionData: DisplayConditionData) => {
      if (doneCallbackRef.current) {
        const currentConditions = getDynamicSegmentIdMap();
        setDynamicSegmentIdMap({
          ...currentConditions,
          [conditionData.id]: convertToLettersOnly(conditionData.id),
        });
        doneCallbackRef.current(conditionData);
        doneCallbackRef.current = null;
      }
      handleClose();
    },
    [handleClose, getDynamicSegmentIdMap, setDynamicSegmentIdMap],
  );

  const onClose = useCallback(() => {
    handleClose();
    doneCallbackRef.current = null;
  }, [handleClose]);

  return {
    isOpen: open,
    onDisplayConditionRequest,
    onConfirm,
    onClose,
    selectedId: dialogState?.selectedId,
    nestedBlocked: dialogState?.nestedBlocked ?? false,
  };
}
